import { Eye, EyeOff } from "lucide-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { GoogleLogin, useGoogleOneTapLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import toast from "react-hot-toast";
import Logo from "../../components/Logo/Logo";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);
  const { login, googleLogin, loading } = useAuth();
  const navigate = useNavigate();
  const googleLoginRef = React.useRef(null);

  // Use Google One Tap to get user info
  useGoogleOneTapLogin({
    onSuccess: (credentialResponse) => {
      try {
        const decoded = jwtDecode(credentialResponse.credential);
        setGoogleUser({
          name: decoded.name,
          email: decoded.email,
          picture: decoded.picture,
        });
      } catch (error) {
        console.error("Error decoding Google token:", error);
      }
    },
    onError: () => {
      // Silently fail - user can still use regular login
    },
  });

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      // Decode to get user info for display
      try {
        const decoded = jwtDecode(credentialResponse.credential);
        setGoogleUser({
          name: decoded.name,
          email: decoded.email,
          picture: decoded.picture,
        });
      } catch (error) {
        console.error("Error decoding Google token:", error);
      }

      const result = await googleLogin(credentialResponse.credential);
      if (result.success) {
        // Redirect based on user role
        const redirectPath =
          result.user?.role === "main_admin" ? "/main-admin" : "/dashboard";
        navigate(redirectPath);
      }
    } catch (error) {
      console.error("Google login error:", error);
      toast.error("Google login failed. Please try again.");
    }
  };

  const handleGoogleError = () => {
    toast.error("Google login failed. Please try again.");
  };

  const triggerGoogleLogin = () => {
    // Find and click the hidden GoogleLogin button
    const googleButton =
      document.querySelector('[data-testid="google-login-button"]') ||
      document.querySelector('div[role="button"][aria-label*="Google"]') ||
      googleLoginRef.current?.querySelector('div[role="button"]');
    if (googleButton) {
      googleButton.click();
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (data) => {
    try {
      console.log("Login attempt with:", {
        email: data.email,
        password: data.password ? "***" : "empty",
        rememberMe,
      });

      const result = await login(data.email, data.password, rememberMe);
      console.log("Login result:", result);

      // Check if login was successful
      if (result?.success) {
        // Redirect based on user role
        const redirectPath =
          result.user?.role === "main_admin" ? "/main-admin" : "/dashboard";
        navigate(redirectPath);
      } else {
        // Error is already handled by AuthContext (toast shown)
        console.log("Login failed:", result?.error || "Unknown error");
        // Don't do anything else - just stay on the page
      }
    } catch (error) {
      // Handle any unexpected errors
      console.error("Unexpected login error:", error);
      toast.error("An unexpected error occurred. Please try again.");
    }

    // Always return false to prevent any form submission
    return false;
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left column - Form (white) - same structure as Register */}
      <div className="w-full md:w-[40%] min-h-screen bg-white flex flex-col">
        <header className="w-full px-6 py-5 flex items-center justify-start flex-shrink-0">
          <Link to="/" className="flex items-center">
            <Logo size={40} showText={false} variant="dark" />
          </Link>
        </header>

        <div className="flex-1 flex items-center justify-center px-6 py-8 md:py-12">
          <div className="w-full max-w-[400px]">
            <h1 className="text-[28px] md:text-[32px] font-bold text-black mb-2">
              Welcome back
            </h1>
            <p className="text-sm text-gray-500 mb-8">
              Enter your details to access your workspace.
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit(onSubmit)(e);
              }}
              className="space-y-4"
            >
              <div ref={googleLoginRef} className="hidden">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  theme="filled_white"
                  size="large"
                  text="signin_with"
                  shape="rectangular"
                  width="100%"
                />
              </div>

              <button
                type="button"
                onClick={triggerGoogleLogin}
                className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 font-medium py-3 px-4 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                <span>Continue with Google</span>
              </button>

              {/* <button
                type="button"
                className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 font-medium py-3 px-4 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors opacity-90"
                onClick={() => toast("Microsoft sign-in coming soon")}
              >
                <svg className="w-5 h-5" viewBox="0 0 23 23">
                  <path fill="#f35325" d="M1 1h10v10H1z" />
                  <path fill="#81bc06" d="M12 1h10v10H12z" />
                  <path fill="#05a6f0" d="M1 12h10v10H1z" />
                  <path fill="#ffba08" d="M12 12h10v10H12z" />
                </svg>
                <span>Continue with Microsoft</span>
              </button> */}

              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white text-gray-500">Or sign in with email</span>
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-[14px] font-medium text-[#374151] mb-1.5 leading-auto tracking-normal">Work Email</label>
                <input
                  id="email"
                  type="email"
                  {...register("email", {
                    required: "Email is required",
                    pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Invalid email address" },
                  })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#18C8C2]/40 focus:border-[#18C8C2]"
                  placeholder="name@company.com"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label htmlFor="password" className="block text-[14px] font-medium text-[#374151] leading-auto tracking-normal">Password</label>
                  <Link to="/forgot-password" className="text-sm text-[#18C8C2] hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    {...register("password", {
                      required: "Password is required",
                      minLength: { value: 8, message: "Password must be at least 8 characters" },
                    })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#18C8C2]/40 focus:border-[#18C8C2] pr-12"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
              </div>

              <div className="flex items-start gap-2">
                <input
                  id="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-[#18C8C2] focus:ring-[#18C8C2]"
                />
                <label htmlFor="remember-me" className="text-sm text-gray-600">
                  Keep me signed in
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 px-4 rounded-lg font-semibold text-white bg-[#18C8C2] hover:bg-[#15b3ad] focus:outline-none focus:ring-2 focus:ring-[#18C8C2]/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    Signing in...
                  </span>
                ) : (
                  "Sign In"
                )}
              </button>

              <p className="text-sm text-gray-600 text-center pt-1">
                Don&apos;t have an account?{" "}
                <Link to="/register" className="text-[#18C8C2] font-medium hover:underline">
                  Sign up 
                  {/* for free */}
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Right column - Testimonial (dark) - same structure as Register, different quote */}
      <div
        className="hidden md:flex md:w-[60%] min-h-screen flex-col justify-between px-8 lg:px-12 py-12 bg-gradient-to-br from-[#0f172a] via-[#0f172a] to-[#1e293b]"
      >
        <div className="flex-1 flex items-center justify-center">
          <div
            className="w-full max-w-[920px] rounded-[16px] bg-white/[0.05] backdrop-blur-sm border border-white/10 shadow-xl"
            style={{ padding: "31.3px 32px 32px 32px" }}
          >
            <p className="text-[16px] font-semibold leading-[24px] text-white/90 mb-6">
              &ldquo;We switched to TeamGrid last year and our team efficiency improved by 40% in the first month<br />alone.&rdquo;
            </p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#14B8A6] flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                MK
              </div>
              <div className="min-w-0 flex flex-col gap-0.5">
                <p className="text-white/90 font-semibold text-[16px] leading-[24px]">Deepanshi Akhani</p>
                <p className="text-white/70 text-[14px] font-normal leading-[20px]">Sr. Manager at Ratnaafin Capital Pvt ltd</p>
              </div>
            </div>
          </div>
        </div>
        <p className="text-white/90 text-sm text-left">
          © {new Date().getFullYear()} TeamGrid Inc. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default Login;
